import React from 'react';
import { ButtonV2, Flex, SpacingStack, TextV2, Toggle } from '@withjoy/joykit';
import { Tip } from '@withjoy/joykit/icons';
import { styles } from '../../CashFundPaymentMethod.style';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { FormikProps } from 'formik';
import { AddCashFundDialogFields } from '../../../../types';
import { DonationFundPlatformTypeEnum } from '@graphql/generated';
import { PaymentMethodOption } from '@shared/utils/paymentMethodOptions';
import { AppPlatformSelect } from './components/AppPaymentPlatformSelect/AppPlatformSelect';
import { useCreditCardSetupContext } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { pxToRem } from '@withjoy/joykit/theme';
import { useShouldShowConnectBankAccountButton } from '@apps/registry/common/utils/registryPayoutUtils';

interface AppPlatformToggleProps {
  formik: FormikProps<AddCashFundDialogFields>;
  isMobile: boolean;
  setPaymentMethod: (paymentMethod?: DonationFundPlatformTypeEnum) => void;
  selectedPaymentMethod: Maybe<PaymentMethodOption>;
  paymentLink: string;
  onUsernameFocus: React.ChangeEventHandler<HTMLInputElement>;
  isEditDialog?: boolean;
}
export const AppPlatformToggle: React.FC<AppPlatformToggleProps> = props => {
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();

  const { appPayment, appPaymentDescription, appPaymentNote, proTipTitle, proTipDescription, connectBankAccount } = getCashFundDialogTranslations();
  const { payoutAccountInfo, initKYCOnboarding, initPayoutAccountLoading } = useCreditCardSetupContext();
  const shouldShowBankAccountButton = useShouldShowConnectBankAccountButton();

  const handleConnectBankAccount = () => {
    if (!payoutAccountInfo) {
      initKYCOnboarding();
    } else {
      withWindow(window => {
        window.open(payoutAccountInfo.onboardingUrl || payoutAccountInfo.serviceDashboardUrl, '_self');
      });
    }
  };

  return (
    <Flex __css={styles.appPlatFormToggleWrapper()}>
      <Flex
        rowGap={5}
        alignItems="center"
        justifyContent="space-between"
      >
        <SpacingStack spacing={1}>
          <Flex alignItems="center">
            <TextV2 typographyVariant="hed1">{appPayment}</TextV2>
          </Flex>
          <TextV2
            typographyVariant="label2"
            color="mono12"
          >
            {appPaymentDescription}
          </TextV2>
          <TextV2
            typographyVariant="label1"
            color="mono8"
          >
            {appPaymentNote}
          </TextV2>
        </SpacingStack>
        <Flex justifyContent="flex-end">
          <Toggle
            label=""
            intent="primary"
            hideLabel={true}
            checked={props.formik.values.enableAppPlatform}
            name="enableAppPlatform"
            onChange={props.formik.handleChange}
          />
        </Flex>
      </Flex>
      {props.formik.values.enableAppPlatform && <AppPlatformSelect {...props} />}
      {!props.formik.values.enableJoyCredit && props.formik.values.enableAppPlatform && (
        <Flex
          flexDirection="column"
          padding={5}
          borderRadius={pxToRem(12)}
          alignItems="center"
          textAlign="center"
          boxShadow={boxShadowValuesV2.xxxl}
        >
          <Tip />
          <TextV2
            typographyVariant="hed1"
            color="mono14"
            marginY={3}
          >
            {proTipTitle}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono12"
          >
            {proTipDescription}
          </TextV2>
          {shouldShowBankAccountButton && (
            <ButtonV2
              marginTop={5}
              width="100%"
              typographyVariant="button1"
              intent="newPrimary"
              shape="rounded"
              size="sm"
              onClick={handleConnectBankAccount}
              loading={initPayoutAccountLoading}
            >
              {connectBankAccount}
            </ButtonV2>
          )}
        </Flex>
      )}
    </Flex>
  );
};
